import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { MessageCircle, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Comments = ({ recipeId }) => {
  const { getLocalizedText } = useLanguage();
  const { user, token, isAuthenticated } = useUser();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  const texts = {
    comments: { fi: 'Kommentit', fr: 'Commentaires', en: 'Comments' },
    writeComment: { fi: 'Kirjoita kommentti...', fr: 'Écrire un commentaire...', en: 'Write a comment...' },
    post: { fi: 'Lähetä', fr: 'Publier', en: 'Post' },
    loginToComment: { fi: 'Kirjaudu kommentoidaksesi', fr: 'Connectez-vous pour commenter', en: 'Sign in to comment' },
    noComments: { fi: 'Ei vielä kommentteja', fr: 'Pas encore de commentaires', en: 'No comments yet' },
    beFirst: { fi: 'Ole ensimmäinen!', fr: 'Soyez le premier!', en: 'Be the first!' }
  };

  useEffect(() => {
    fetchComments();
  }, [recipeId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/comments/${recipeId}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/comments`,
        { recipe_id: recipeId, text: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success(getLocalizedText({ fi: 'Kommentti lisätty!', fr: 'Commentaire ajouté!', en: 'Comment added!' }));
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(comments.filter(c => c.id !== commentId));
      toast.success(getLocalizedText({ fi: 'Kommentti poistettu', fr: 'Commentaire supprimé', en: 'Comment deleted' }));
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-6" data-testid="comments-section">
      <h3 className="text-xl font-semibold flex items-center gap-2">
        <MessageCircle className="h-5 w-5" />
        {getLocalizedText(texts.comments)} ({comments.length})
      </h3>

      {/* Comment Form */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="space-y-3">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={getLocalizedText(texts.writeComment)}
            className="min-h-[100px] resize-none"
            data-testid="comment-input"
          />
          <Button 
            type="submit" 
            disabled={loading || !newComment.trim()}
            className="rounded-full"
            data-testid="post-comment-btn"
          >
            {getLocalizedText(texts.post)}
          </Button>
        </form>
      ) : (
        <div className="p-4 bg-muted/20 rounded-xl text-center">
          <p className="text-foreground/60">{getLocalizedText(texts.loginToComment)}</p>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-foreground/50">
            <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>{getLocalizedText(texts.noComments)}</p>
            <p className="text-sm">{getLocalizedText(texts.beFirst)}</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div 
              key={comment.id} 
              className="bg-card p-4 rounded-xl border border-border"
              data-testid={`comment-${comment.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{comment.user_name}</p>
                    <p className="text-xs text-foreground/50">{formatDate(comment.created_at)}</p>
                  </div>
                </div>
                {user && user.id === comment.user_id && (
                  <button
                    onClick={() => handleDelete(comment.id)}
                    className="text-foreground/40 hover:text-destructive transition-colors"
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="mt-3 text-foreground/80 whitespace-pre-wrap">{comment.text}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
